/**
 * Tough-Bar Builder - Accessories Module (Step 3)
 * Accessory selection and configuration methods
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

window.ToughBarBuilder.accessories = {
  normalizeAccessoryDependencyScope(scope) {
    if (typeof scope !== 'string') return null;
    const normalized = scope.trim().toLowerCase();
    if (!normalized) return null;

    if (['global', 'shared', 'agnostic', 'independent'].includes(normalized)) {
      return 'global';
    }

    if (['device', 'main_device', 'context', 'contextual', 'tablet', 'dependent'].includes(normalized)) {
      return 'device';
    }

    return null;
  },

  normalizeAccessoryDependencyKeys(keys) {
    if (!Array.isArray(keys)) return [];

    const normalized = keys
      .map(key => String(key || '').trim().toLowerCase())
      .filter(Boolean)
      .map(key => {
        if (['device', 'device_selection', 'main_device'].includes(key)) return 'main_device';
        if (['tablet', 'tablet_selection'].includes(key)) return 'tablet_selection';
        return key;
      });

    return [...new Set(normalized)];
  },

  normalizeAccessoryId(accessoryId) {
    if (accessoryId === undefined || accessoryId === null) return '';
    return String(accessoryId).trim().replace(/___+/g, '_');
  },

  getKeyboardAccessoryButtonId() {
    return 'tablet_keyboard';
  },

  isKeyboardAccessoryId(accessoryId) {
    const normalized = this.normalizeAccessoryId(accessoryId).toLowerCase();
    if (!normalized) return false;
    return normalized === this.getKeyboardAccessoryButtonId()
      || /(^|_)keyboards?(_|$)/.test(normalized)
      || /keyboards?/.test(normalized);
  },

  isAccessoryIdMatch(leftId, rightId) {
    if (!leftId || !rightId) return false;
    return this.normalizeAccessoryId(leftId) === this.normalizeAccessoryId(rightId);
  },

  findSelectedAccessoryIndex(accessoryId) {
    if (!accessoryId) return -1;
    return this.selectedAccessories.findIndex(accessory => this.isAccessoryIdMatch(accessory.id, accessoryId));
  },

  requestSideCartPositionRecalc() {
    if (typeof this.recalculateSideCartPosition !== 'function') return;
    this.$nextTick(() => this.recalculateSideCartPosition());
  },

  getAccessoryRequestKey(type, accessoryId = '*') {
    const requestType = String(type || 'generic').trim().toLowerCase();
    const normalizedAccessoryId = accessoryId === '*' ? '*' : this.normalizeAccessoryId(accessoryId);
    return `${requestType}:${normalizedAccessoryId || '*'}`;
  },

  nextAccessoryRequestToken(type, accessoryId = '*') {
    if (!this._accessoryRequestTokens || typeof this._accessoryRequestTokens !== 'object') {
      this._accessoryRequestTokens = {};
    }

    const key = this.getAccessoryRequestKey(type, accessoryId);
    const value = Number(this._accessoryRequestTokens[key] || 0) + 1;
    this._accessoryRequestTokens[key] = value;

    return { key, value };
  },

  isAccessoryRequestTokenActive(token) {
    if (!token || !token.key) return false;
    return Number(this._accessoryRequestTokens?.[token.key] || 0) === Number(token.value || 0);
  },

  invalidateAccessoryRequests(accessoryId) {
    this.nextAccessoryRequestToken('customize-availability', accessoryId);
    this.nextAccessoryRequestToken('modal-product', accessoryId);
    this.nextAccessoryRequestToken('customize-modal', accessoryId);
  },

  getAccessoryButton(accessoryId) {
    if (!accessoryId) return null;
    const buttons = Array.isArray(this.accessoryButtons) ? this.accessoryButtons : [];
    const button = buttons.find(item => this.isAccessoryIdMatch(item.id, accessoryId)) || null;
    if (button) return button;
    if (this.isKeyboardAccessoryId(accessoryId) && this.canShowKeyboardAccessoryButton()) {
      return this.getVirtualKeyboardAccessoryButton();
    }
    return null;
  },

  isKeyboardAccessoryButton(accessoryOrId = this.accessoryCustomizeTarget) {
    const button = accessoryOrId && typeof accessoryOrId === 'object'
      ? accessoryOrId
      : (Array.isArray(this.accessoryButtons)
          ? this.accessoryButtons.find(item => this.isAccessoryIdMatch(item.id, accessoryOrId))
          : null);
    const rawId = accessoryOrId && typeof accessoryOrId === 'object'
      ? accessoryOrId.id
      : accessoryOrId;
    const haystack = [
      rawId,
      button?.id,
      button?.label,
      button?.title,
      button?.subtitle,
      button?.handle
    ].filter(Boolean).join(' ').toLowerCase();

    return this.isKeyboardAccessoryId(rawId) || /keyboards?/.test(haystack);
  },

  getVirtualKeyboardAccessoryButton() {
    return {
      id: this.getKeyboardAccessoryButtonId(),
      label: 'Keyboard',
      type: 'keyboard',
      default_subtitle: 'Choose a keyboard-compatible tablet configuration',
      dependency_scope: 'device',
      depends_on: ['main_device', 'tablet_selection']
    };
  },

  getAccessoryButtonsWithKeyboardOption(buttons = []) {
    const sourceButtons = Array.isArray(buttons) ? buttons : [];
    const hasKeyboardButton = sourceButtons.some(button => this.isKeyboardAccessoryButton(button));
    if (hasKeyboardButton || !this.canShowKeyboardAccessoryButton()) return sourceButtons;

    const nextButtons = [...sourceButtons, this.getVirtualKeyboardAccessoryButton()];
    if (typeof this.getAccessoryStepSortPriority !== 'function') return nextButtons;

    return nextButtons
      .map((button, index) => ({
        button,
        index,
        priority: this.getAccessoryStepSortPriority(button)
      }))
      .sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a.index - b.index;
      })
      .map(entry => entry.button);
  },

  isKeyboardAccessoryCustomizeModal() {
    if (this.modal?.type !== 'accessory-customize') return false;
    const accessoryId = this.accessoryCustomizeTarget || this.modal?.accessoryId || this.accessoryModal?.accessoryId;
    return this.isKeyboardAccessoryButton(accessoryId || this.currentAccessoryButton);
  },

  getAccessoryPrimarySelectionMeta(accessoryOrButton = null) {
    const button = accessoryOrButton && typeof accessoryOrButton === 'object'
      ? accessoryOrButton
      : this.getAccessoryButton(accessoryOrButton);
    const modalConfig = button?.modal && typeof button.modal === 'object' ? button.modal : null;
    const legacyConfig = button?.modal_options && typeof button.modal_options === 'object'
      ? button.modal_options
      : null;
    const modalFields = Array.isArray(modalConfig?.dropdowns) ? modalConfig.dropdowns : [];
    const legacyFields = Array.isArray(legacyConfig?.fields) ? legacyConfig.fields : [];
    const modalOptions = modalConfig?.options && typeof modalConfig.options === 'object'
      ? modalConfig.options
      : {};
    const legacyOptions = legacyConfig?.options && typeof legacyConfig.options === 'object'
      ? legacyConfig.options
      : {};

    return {
      title: typeof modalConfig?.title === 'string' ? modalConfig.title : '',
      fields: modalFields.length > 0 ? modalFields : legacyFields,
      options: Object.keys(modalOptions).length > 0 ? modalOptions : legacyOptions
    };
  },

  normalizeAccessoryCustomizationPayload(data) {
    if (!data || typeof data !== 'object') return null;
    const customization = data.customization;
    if (customization && typeof customization === 'object') {
      return customization;
    }
    return data;
  },

  getAccessoryButtonsFromPayload(payload = null) {
    const buttons = payload?.section?.buttons;
    if (!Array.isArray(buttons) || buttons.length === 0) return [];

    const reservedIds = new Set(['tablet', 'laptop', 'other_device', 'main_device', 'vehicle']);
    return buttons.filter(button => {
      const id = String(button?.id || '').trim();
      if (!id || reservedIds.has(id)) return false;
      return true;
    });
  },

  getResponseAccessoryConfig(accessoryId, payload = null) {
    if (!accessoryId) return null;

    const responseAccessories = payload?.context?.accessories;
    if (!responseAccessories || typeof responseAccessories !== 'object') {
      return null;
    }

    const match = Object.entries(responseAccessories).find(([key]) => this.isAccessoryIdMatch(key, accessoryId));
    if (!match) return null;

    const config = match[1];
    return config && typeof config === 'object' ? config : null;
  },

  async ensureAccessoryButtonsCache() {
    const existing = Array.isArray(this.accessoryButtonsCache) ? this.accessoryButtonsCache : [];
    if (existing.length > 0) return true;

    try {
      const body = this.buildApiContext({ current_step: 3 });
      delete body.accessories_complete;

      const data = await this.postToBuilder(this.getBuilderEndpoint(), body);
      const buttons = this.getAccessoryButtonsFromPayload(data);
      if (buttons.length > 0) {
        this.accessoryButtonsCache = buttons;
        return true;
      }
    } catch (error) {
      console.warn('[Tough-Bar Accessories] Failed to hydrate accessory buttons cache:', error);
    }

    return false;
  },

  buildAccessoryDependencyMeta(accessoryId) {
    const button = this.getAccessoryButton(accessoryId);
    const scope = this.normalizeAccessoryDependencyScope(
      button?.dependency_scope || button?.scope || button?.context_scope || null
    );
    const dependsOn = this.normalizeAccessoryDependencyKeys(
      button?.depends_on || button?.dependencies || button?.context_keys || []
    );

    return {
      dependencyScope: scope,
      dependsOn: dependsOn
    };
  },

  formatAccessoryDisplaySegment(value) {
    if (value === undefined || value === null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value).trim();
  },

  getAccessoryPrimaryDisplayValue(accessoryId, config = {}) {
    const button = this.getAccessoryButton(accessoryId);
    const safeConfig = config && typeof config === 'object' ? config : {};
    const make = this.formatAccessoryDisplaySegment(safeConfig.make);
    const model = this.formatAccessoryDisplaySegment(safeConfig.model);
    const caseType = this.formatAccessoryDisplaySegment(safeConfig.case_type);
    const hasModalStyleFields = !!(make || model || caseType);
    const isModalType = button?.type === 'modal' || (!button && hasModalStyleFields);
    if (isModalType) {
      let label = [make, model].filter(Boolean).join(' ').trim();
      if (caseType && caseType.toUpperCase() !== 'N/A') {
        label = label ? `${label} - ${caseType}` : caseType;
      }
      return label;
    }

    const preferredKeys = [
      'base_type',
      'mount_type',
      'holder_type',
      'connection_type',
      'series',
      'dock_style',
      'power_type',
      'peripheral_support',
      'keyboard_type',
      'swing_arms'
    ];

    for (const key of preferredKeys) {
      const value = this.formatAccessoryDisplaySegment(safeConfig[key]);
      if (value) return value;
    }

    const ignoredKeys = new Set(['products', 'addons', 'images', 'image', 'alert', 'preload']);
    for (const [key, rawValue] of Object.entries(safeConfig)) {
      if (ignoredKeys.has(key)) continue;
      const value = this.formatAccessoryDisplaySegment(rawValue);
      if (value) return value;
    }

    return this.formatAccessoryDisplaySegment(button?.subtitle);
  },

  buildAccessoryDisplayValue(accessoryId, config = {}, products = []) {
    const button = this.getAccessoryButton(accessoryId);
    const primaryLabel = this.getAccessoryPrimaryDisplayValue(accessoryId, config);
    const handles = Array.isArray(products)
      ? products
      : (Array.isArray(config?.products) ? config.products : []);
    const handle = this.formatAccessoryDisplaySegment(handles[0]);

    const hasModalStyleFields = !!(
      this.formatAccessoryDisplaySegment(config?.make)
      || this.formatAccessoryDisplaySegment(config?.model)
      || this.formatAccessoryDisplaySegment(config?.case_type)
    );
    const isModalType = button?.type === 'modal' || (!button && hasModalStyleFields);
    if (isModalType) {
      return primaryLabel || handle || '';
    }

    if (primaryLabel && handle) return `${primaryLabel} - ${handle}`;
    return primaryLabel || handle || '';
  },

  getAccessoryUnselectedSubtitle(button) {
    if (!button || typeof button !== 'object') return '';

    // Modal accessories should keep their guidance copy (e.g., "Enter your make and model").
    if (button.type === 'modal') {
      return this.formatAccessoryDisplaySegment(button.subtitle);
    }

    // Non-modal accessories should only show an explicit default subtitle; otherwise keep blank.
    return this.formatAccessoryDisplaySegment(
      button.default_subtitle || button.subtitle_default || button.initial_subtitle
    );
  },

  syncAccessorySelectionToSummaryKit(accessoryId) {
    if (!Array.isArray(this.response?.kits) || !accessoryId) return;

    const selectedAccessory = this.getSelectedAccessory(accessoryId);
    if (!selectedAccessory) return;

    const summaryKitIndex = this.response.kits.findIndex(kit => this.isAccessoryIdMatch(kit?.id, accessoryId));
    if (summaryKitIndex === -1) return;

    const currentKit = this.response.kits[summaryKitIndex] || {};
    const button = this.getAccessoryButton(accessoryId);
    const nextDisplay = selectedAccessory.displayValue
      || this.buildAccessoryDisplayValue(accessoryId, selectedAccessory.config || {}, selectedAccessory.products || [])
      || currentKit.display
      || '';

    const nextKit = {
      ...currentKit,
      id: currentKit.id || selectedAccessory.id,
      label: currentKit.label || button?.label || selectedAccessory.id,
      display: nextDisplay,
      products: Array.isArray(selectedAccessory.products) ? [...selectedAccessory.products] : [],
      selection: {
        ...(currentKit.selection || {}),
        ...(selectedAccessory.config || {})
      }
    };

    const nextKits = [...this.response.kits];
    nextKits.splice(summaryKitIndex, 1, nextKit);
    this.response = {
      ...this.response,
      kits: nextKits
    };
  },

  inferAccessoryDeviceDependency(accessory) {
    if (!accessory) return false;

    const scope = this.normalizeAccessoryDependencyScope(accessory.dependencyScope);
    if (scope === 'global') return false;
    if (scope === 'device') return true;

    const dependsOn = this.normalizeAccessoryDependencyKeys(accessory.dependsOn);
    if (dependsOn.length > 0) return true;

    const config = accessory.config || {};
    const tabletSignalKeys = [
      'make',
      'model',
      'case_type',
      'tablet_make',
      'tablet_model',
      'tablet_case_type'
    ];

    return tabletSignalKeys.some(key => {
      const value = config[key];
      return value !== undefined && value !== null && value !== '';
    });
  },

  shouldPruneAccessoryForContextChange(accessory, changedContextKeys = []) {
    if (!accessory) return false;

    const normalizedChanged = this.normalizeAccessoryDependencyKeys(changedContextKeys);
    const scope = this.normalizeAccessoryDependencyScope(accessory.dependencyScope);
    const dependsOn = this.normalizeAccessoryDependencyKeys(accessory.dependsOn);

    if (scope === 'global') return false;
    if (scope === 'device') return true;

    if (dependsOn.length > 0) {
      if (normalizedChanged.length === 0) return true;
      return normalizedChanged.some(key => dependsOn.includes(key));
    }

    const inferredDeviceDependent = this.inferAccessoryDeviceDependency(accessory);
    if (!inferredDeviceDependent) return false;

    if (normalizedChanged.length === 0) return true;
    return normalizedChanged.some(key => key === 'main_device' || key === 'tablet_selection' || key.startsWith('tablet_'));
  },

  pruneAccessoriesForContextChange(changedContextKeys = []) {
    if (!Array.isArray(this.selectedAccessories) || this.selectedAccessories.length === 0) {
      return [];
    }

    const removed = [];
    this.selectedAccessories = this.selectedAccessories.filter((accessory, index) => {
      const shouldPrune = this.shouldPruneAccessoryForContextChange(accessory, changedContextKeys);
      if (shouldPrune) {
        removed.push({ index, accessory });
        return false;
      }
      return true;
    });

    return removed;
  },

  restorePrunedAccessories(removedAccessories = []) {
    if (!Array.isArray(removedAccessories) || removedAccessories.length === 0) return;

    const restored = [...this.selectedAccessories];
    const ordered = [...removedAccessories].sort((a, b) => a.index - b.index);

    ordered.forEach(entry => {
      const insertAt = Math.max(0, Math.min(entry.index, restored.length));
      restored.splice(insertAt, 0, entry.accessory);
    });

    this.selectedAccessories = restored;
  },

  hydrateAccessoriesFromSummaryKits(options = {}) {
    const { overwrite = false } = options;
    const summaryKits = Array.isArray(this.response?.kits) ? this.response.kits : [];
    if (summaryKits.length === 0) {
      if (overwrite) {
        this.selectedAccessories = [];
        if (typeof this.clearAccessoryCustomizeAvailability === 'function') {
          this.clearAccessoryCustomizeAvailability();
        }
      }
      return;
    }

    const reservedIds = new Set(['vehicle', 'tablet', 'laptop', 'other_device', 'main_device']);
    const nextAccessories = overwrite ? [] : [...(this.selectedAccessories || [])];
    let didChange = overwrite;

    summaryKits.forEach(kit => {
      const kitId = String(kit?.id || '').trim();
      if (!kitId || reservedIds.has(kitId)) return;
      if (!kit?.selection || typeof kit.selection !== 'object') return;

      const existing = nextAccessories.find(accessory => this.isAccessoryIdMatch(accessory.id, kitId)) || null;
      const button = this.getAccessoryButton(kitId);
      const responseConfig = this.getResponseAccessoryConfig(kitId, this.response) || {};
      const selection = { ...kit.selection };
      const fallbackConfig = button?.type === 'modal' ? {} : (button?.preload || button?.options?.[0] || {});
      const config = {
        ...(fallbackConfig || {}),
        ...(existing?.config || {}),
        ...(responseConfig || {}),
        ...(selection || {})
      };
      const products = Array.isArray(kit.products)
        ? kit.products.filter(Boolean)
        : (Array.isArray(selection.products) ? selection.products.filter(Boolean) : []);
      const primaryHandle = products[0] || null;
      const productData = primaryHandle ? (this.products?.[primaryHandle] || null) : null;
      const existingIndex = nextAccessories.findIndex(accessory => this.isAccessoryIdMatch(accessory.id, kitId));
      const resolvedProducts = products.length > 0
        ? products
        : (Array.isArray(responseConfig?.products) ? responseConfig.products.filter(Boolean) : (existing?.products || []));
      const resolvedPrimaryHandle = resolvedProducts[0] || null;
      const cachedProduct = resolvedPrimaryHandle ? (this.products?.[resolvedPrimaryHandle] || null) : null;
      const existingProductData = existing?.productData;
      const sameHandleAsExistingProduct = !!(resolvedPrimaryHandle && existingProductData?.handle === resolvedPrimaryHandle);
      const resolvedProductData = sameHandleAsExistingProduct
        ? existingProductData
        : (cachedProduct || productData);
      const dependencyMeta = this.buildAccessoryDependencyMeta(kitId);
      const displayValue = this.buildAccessoryDisplayValue(kitId, config, resolvedProducts)
        || kit.display
        || existing?.displayValue
        || null;
      const hasModalInputs = !!(
        this.formatAccessoryDisplaySegment(config?.make)
        || this.formatAccessoryDisplaySegment(config?.model)
        || this.formatAccessoryDisplaySegment(config?.case_type)
      );
      const needsModalProductResolution = button?.type === 'modal'
        && hasModalInputs
        && resolvedProducts.length === 0;

      const nextAccessory = {
        ...(existing || {}),
        id: existing?.id || kitId,
        config,
        products: [...resolvedProducts],
        productData: resolvedProductData,
        loading: needsModalProductResolution || !!(resolvedPrimaryHandle && !resolvedProductData),
        error: null,
        displayValue,
        dependencyScope: existing?.dependencyScope || dependencyMeta.dependencyScope,
        dependsOn: existing?.dependsOn || dependencyMeta.dependsOn
      };

      if (existingIndex === -1) {
        nextAccessories.push(nextAccessory);
        didChange = true;
        return;
      }

      nextAccessories[existingIndex] = nextAccessory;
      didChange = true;
    });

    if (overwrite && typeof this.clearAccessoryCustomizeAvailability === 'function') {
      this.clearAccessoryCustomizeAvailability();
    }

    if (didChange) {
      this.selectedAccessories = nextAccessories;
    }
  },

  hydrateAccessoriesFromStepButtons(options = {}) {
    const { overwrite = false } = options;
    const buttons = this.getAccessoryButtonsFromPayload(this.response);
    if (!Array.isArray(buttons) || buttons.length === 0) {
      if (overwrite) {
        this.selectedAccessories = [];
        if (typeof this.clearAccessoryCustomizeAvailability === 'function') {
          this.clearAccessoryCustomizeAvailability();
        }
      }
      return;
    }

    const selectedButtons = buttons.filter(button => button?.selected);
    const existingAccessories = overwrite ? [] : (Array.isArray(this.selectedAccessories) ? [...this.selectedAccessories] : []);
    const nextAccessories = selectedButtons.map(button => {
      const accessoryId = String(button?.id || '').trim();
      if (!accessoryId) return null;

      const existing = existingAccessories.find(accessory => this.isAccessoryIdMatch(accessory?.id, accessoryId)) || null;
      const responseConfig = this.getResponseAccessoryConfig(accessoryId, this.response) || {};
      const selectionConfig = button?.selection && typeof button.selection === 'object' ? button.selection : {};
      const fallbackConfig = (button?.type === 'modal') ? {} : (button?.preload || button?.options?.[0] || {});
      const config = {
        ...(fallbackConfig || {}),
        ...(existing?.config || {}),
        ...(responseConfig || {}),
        ...(selectionConfig || {})
      };

      const productCandidates = [
        selectionConfig?.products,
        responseConfig?.products,
        config?.products,
        existing?.products
      ];
      const products = productCandidates.find(candidate => Array.isArray(candidate) && candidate.length > 0) || [];
      const primaryHandle = products[0] || null;
      const cachedProduct = primaryHandle ? (this.products?.[primaryHandle] || null) : null;
      const existingProductData = existing?.productData;
      const sameHandleAsExistingProduct = !!(primaryHandle && existingProductData?.handle === primaryHandle);
      const productData = sameHandleAsExistingProduct
        ? existingProductData
        : (cachedProduct || null);

      const dependencyMeta = this.buildAccessoryDependencyMeta(accessoryId);
      const displayValue = this.buildAccessoryDisplayValue(accessoryId, config, products)
        || button?.subtitle
        || existing?.displayValue
        || null;

      const hasModalInputs = !!(
        this.formatAccessoryDisplaySegment(config?.make)
        || this.formatAccessoryDisplaySegment(config?.model)
        || this.formatAccessoryDisplaySegment(config?.case_type)
      );
      const needsModalProductResolution = button?.type === 'modal'
        && hasModalInputs
        && products.length === 0;

      return {
        ...(existing || {}),
        id: existing?.id || accessoryId,
        config,
        products: [...products],
        productData,
        loading: needsModalProductResolution || !!(primaryHandle && !productData),
        error: null,
        displayValue,
        dependencyScope: existing?.dependencyScope || dependencyMeta.dependencyScope,
        dependsOn: existing?.dependsOn || dependencyMeta.dependsOn
      };
    }).filter(Boolean);

    this.selectedAccessories = nextAccessories;

    nextAccessories.forEach(accessory => {
      const handle = accessory?.products?.[0];
      if (!handle) {
        const button = this.getAccessoryButton(accessory?.id);
        const hasModalInputs = !!(
          this.formatAccessoryDisplaySegment(accessory?.config?.make)
          || this.formatAccessoryDisplaySegment(accessory?.config?.model)
          || this.formatAccessoryDisplaySegment(accessory?.config?.case_type)
        );
        if (button?.type === 'modal' && hasModalInputs) {
          this.fetchModalAccessoryProduct(accessory.id, accessory.config || {});
        }
        return;
      }

      if (accessory?.productData || accessory?.loading !== true) return;
      this.fetchAccessoryProductData(accessory.id, handle);
    });
  },

  /**
   * Replace a selected accessory by ID to keep Alpine reactivity and avoid stale index writes
   * @param {string} accessoryId - The accessory button ID
   * @param {object} updates - Partial updates for the selected accessory
   */
  replaceSelectedAccessory(accessoryId, updates) {
    const index = this.findSelectedAccessoryIndex(accessoryId);
    if (index === -1) return false;

    const current = this.selectedAccessories[index];
    this.selectedAccessories.splice(index, 1, {
      ...current,
      ...updates
    });

    return true;
  },

  /**
   * Toggle accessory selection
   * @param {string} accessoryId - The accessory button ID
   */
  async toggleAccessory(accessoryId) {
    const button = this.getAccessoryButton(accessoryId);
    if (!button) return;

    if (this.isKeyboardAccessoryButton(button)) {
      if (!this.isAccessorySelected(accessoryId) && !this.canSelectMoreAccessories) return;
      await this.openKeyboardAccessoryCustomizeModal(accessoryId);
      return;
    }

    const existingIndex = this.findSelectedAccessoryIndex(accessoryId);

    if (existingIndex !== -1) {
      // Already selected - remove it
      this.selectedAccessories.splice(existingIndex, 1);
      this.clearAccessoryCustomizeAvailability(accessoryId);
      this.requestSideCartPositionRecalc();
      this.scheduleAccessorySnapshotRefresh();
    } else {
      // Not selected - check if we can add more
      if (!this.canSelectMoreAccessories) return;

      // Handle based on type
      if (button.type === 'modal') {
        // Open modal for configuration
        this.openAccessoryModal(accessoryId);
      } else if (button.type === 'configurable' || button.type === 'simple') {
        // Use preload config or first option
        const config = button.preload || button.options?.[0] || {};
        const dependencyMeta = this.buildAccessoryDependencyMeta(accessoryId);
        const accessory = {
          id: accessoryId,
          config: config,
          displayValue: this.buildAccessoryDisplayValue(accessoryId, config, config.products || []),
          products: config.products || [],
          productData: null,
          loading: true,
          dependencyScope: dependencyMeta.dependencyScope,
          dependsOn: dependencyMeta.dependsOn
        };
        this.selectedAccessories.push(accessory);
        this.requestSideCartPositionRecalc();

        // Fetch product data for the first product in the config
        if (config.products && config.products.length > 0) {
          await this.fetchAccessoryProductData(accessoryId, config.products[0]);
        }
        this.scheduleAccessorySnapshotRefresh();
      }
    }
  },

  /**
   * Fetch product data for an accessory
   * @param {string} accessoryId - The accessory button ID
   * @param {string} handle - The product handle/SKU
   */
  async fetchAccessoryProductData(accessoryId, handle) {
    if (!this.isAccessorySelected(accessoryId) || !handle) return;

    const canApplyProductResult = () => {
      const selected = this.getSelectedAccessory(accessoryId);
      if (!selected) return false;
      const currentHandle = selected.products?.[0];
      return currentHandle === handle;
    };

    try {
      let product;

      // Check if we already have this product cached
      if (this.products[handle]) {
        product = this.products[handle];
      } else {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) {
          product = await res.json();
          this.products[handle] = product;
        } else {
          console.warn(`[Tough-Bar Accessories] Product not found: ${handle} (${res.status})`);
        }
      }

      if (!canApplyProductResult()) return;

      if (product) {
        this.replaceSelectedAccessory(accessoryId, {
          productData: product,
          loading: false,
          error: null
        });
      } else {
        this.replaceSelectedAccessory(accessoryId, {
          loading: false,
          error: 'Product not found'
        });
      }
    } catch (err) {
      console.error(`[Tough-Bar Accessories] Failed to fetch product ${handle}:`, err);

      if (!canApplyProductResult()) return;

      this.replaceSelectedAccessory(accessoryId, {
        loading: false,
        error: err.message
      });
    }
  },

  /**
   * Check if an accessory is selected
   * @param {string} accessoryId - The accessory button ID
   */
  isAccessorySelected(accessoryId) {
    if (this.isKeyboardAccessoryButton(accessoryId)) {
      return this.isKeyboardAccessoryContextSelected();
    }

    return this.findSelectedAccessoryIndex(accessoryId) !== -1;
  },

  /**
   * Get selected accessory by ID
   * @param {string} accessoryId - The accessory button ID
   */
  getSelectedAccessory(accessoryId) {
    if (this.isKeyboardAccessoryButton(accessoryId)) {
      return this.getSelectedKeyboardAccessory();
    }

    return this.selectedAccessories.find(accessory => this.isAccessoryIdMatch(accessory.id, accessoryId));
  },

  scheduleAccessorySnapshotRefresh() {
    if (this.step < 2) return;
    if (typeof this.syncSnapshotWithCurrentState !== 'function') return;
    if (this.snapshotSuppressUrlUpdates) return;

    if (this._accessorySnapshotRefreshTimer) {
      clearTimeout(this._accessorySnapshotRefreshTimer);
    }

    this._accessorySnapshotRefreshTimer = setTimeout(async () => {
      this._accessorySnapshotRefreshTimer = null;
      if (this._accessorySnapshotRefreshInFlight) {
        this._accessorySnapshotRefreshPending = true;
        return;
      }

      this._accessorySnapshotRefreshInFlight = true;

      try {
        await this.syncSnapshotWithCurrentState(true);
      } catch (snapshotError) {
        console.warn('[Tough-Bar Accessories] Snapshot refresh failed:', snapshotError);
      } finally {
        this._accessorySnapshotRefreshInFlight = false;
        if (this._accessorySnapshotRefreshPending) {
          this._accessorySnapshotRefreshPending = false;
          this.scheduleAccessorySnapshotRefresh();
        }
      }
    }, 250);
  },

  clearAccessoryCustomizeAvailability(accessoryId = null) {
    if (!accessoryId) {
      this.accessoryCustomizeAvailability = {};
      return;
    }

    const next = { ...(this.accessoryCustomizeAvailability || {}) };
    const normalizedId = this.normalizeAccessoryId(accessoryId);
    let didDelete = false;

    Object.keys(next).forEach(key => {
      if (this.normalizeAccessoryId(key) !== normalizedId) return;
      delete next[key];
      didDelete = true;
    });

    if (didDelete) {
      this.accessoryCustomizeAvailability = next;
    }
  },

  getAccessoryCustomizePrimaryKeys(accessoryId) {
    const button = this.getAccessoryButton(accessoryId);
    return ((this.getAccessoryPrimarySelectionMeta(button).fields || [])
      .map(field => field?.key)
      .filter(Boolean));
  },

  hasAccessoryCustomizeOptions(accessoryId, customizationData = null) {
    const data = this.normalizeAccessoryCustomizationPayload(customizationData || {}) || {};
    const entries = Array.isArray(data.entries) ? data.entries : [];
    if (entries.length === 0) return false;

    const primaryKeys = new Set(this.getAccessoryCustomizePrimaryKeys(accessoryId));
    const ignoredKeys = new Set(['products', 'addons', 'images', 'image', 'alert', 'preload']);
    const candidateKeys = new Set();

    const addCandidateKey = key => {
      if (!key || primaryKeys.has(key) || ignoredKeys.has(key)) return;
      candidateKeys.add(key);
    };

    (Array.isArray(data.fields) ? data.fields : []).forEach(field => addCandidateKey(field?.key));
    entries.forEach(entry => Object.keys(entry || {}).forEach(addCandidateKey));

    for (const key of candidateKeys) {
      const fieldType = (Array.isArray(data.fields)
        ? data.fields.find(field => field?.key === key)?.type
        : 'select') || 'select';
      const values = [];

      entries.forEach(entry => {
        const value = this.normalizeAccessoryCustomizeValue(entry?.[key], fieldType);
        if (value === null) return;
        if (!values.some(existing => this.accessoryCustomizeValuesEqual(existing, value))) {
          values.push(value);
        }
      });

      if (values.length > 1) return true;
    }

    return false;
  },

  async refreshAccessoryCustomizeAvailability(accessoryId, accessoryConfig = null) {
    if (!accessoryId) return false;
    if (!this.getSelectedAccessory(accessoryId)) return false;

    const requestToken = this.nextAccessoryRequestToken('customize-availability', accessoryId);
    const config = accessoryConfig || this.getSelectedAccessory(accessoryId)?.config || {};
    this.accessoryCustomizeAvailability = {
      ...(this.accessoryCustomizeAvailability || {}),
      [accessoryId]: false
    };

    try {
      const body = this.buildAccessoryCustomizeRequestBody(accessoryId, config);
      const data = await this.postToBuilder(this.getBuilderEndpoint(), body);
      if (data?.error) {
        throw new Error(data.error);
      }

      if (!this.isAccessoryRequestTokenActive(requestToken)) return false;
      if (!this.getSelectedAccessory(accessoryId)) return false;
      const hasOptions = this.hasAccessoryCustomizeOptions(accessoryId, data);
      this.accessoryCustomizeAvailability = {
        ...(this.accessoryCustomizeAvailability || {}),
        [accessoryId]: hasOptions
      };

      return hasOptions;
    } catch (err) {
      console.warn(`[Tough-Bar Accessories] Failed to check customize availability for ${accessoryId}:`, err);
      if (!this.isAccessoryRequestTokenActive(requestToken)) return false;
      if (!this.getSelectedAccessory(accessoryId)) return false;
      this.accessoryCustomizeAvailability = {
        ...(this.accessoryCustomizeAvailability || {}),
        [accessoryId]: true
      };
      return true;
    }
  },

  canShowAccessoryCustomize(accessoryId) {
    if (!this.isAccessorySelected(accessoryId)) return false;
    const button = this.getAccessoryButton(accessoryId);
    const availabilityEntry = Object.entries(this.accessoryCustomizeAvailability || {})
      .find(([id]) => this.isAccessoryIdMatch(id, accessoryId));
    const available = availabilityEntry?.[1];

    if (this.isKeyboardAccessoryButton(button || accessoryId)) {
      return true;
    }

    if (!button) {
      if (available === true) return true;
      if (available === false) return false;
      return true;
    }

    if (button.type === 'configurable') {
      const options = Array.isArray(button.options) ? button.options : [];
      return options.length > 1 || !!button.customize?.enabled;
    }

    if (button.type !== 'modal') {
      if (available === true) return true;
      return !!button.customize?.enabled;
    }

    if (available === true) return true;
    if (available === false) return false;

    return !!button.customize?.enabled;
  },

  isModalAccessoryButton(accessoryId = this.accessoryCustomizeTarget) {
    return this.getAccessoryButton(accessoryId)?.type === 'modal';
  },

  buildAccessoryModalState(accessoryId, config = {}) {
    const safeConfig = config && typeof config === 'object' ? config : {};
    return {
      accessoryId: accessoryId || null,
      make: this.formatAccessoryDisplaySegment(safeConfig.make),
      model: this.formatAccessoryDisplaySegment(safeConfig.model),
      case_type: this.formatAccessoryDisplaySegment(safeConfig.case_type)
    };
  },

  getAccessoryModalSelectionConfig() {
    const modalState = this.accessoryModal || {};
    const config = {};

    if (modalState.make) config.make = modalState.make;
    if (modalState.model) config.model = modalState.model;
    if (modalState.case_type) config.case_type = modalState.case_type;

    return config;
  },

  getAccessoryModalSelectionSignature(accessoryId = this.accessoryCustomizeTarget, config = null) {
    const resolvedConfig = config && typeof config === 'object'
      ? config
      : this.getAccessoryModalSelectionConfig();

    return [
      this.normalizeAccessoryId(accessoryId),
      resolvedConfig.make || '',
      resolvedConfig.model || '',
      resolvedConfig.case_type || ''
    ].join('||');
  },

  getAccessoryCustomizeSelectionSeed(accessoryId = this.accessoryCustomizeTarget) {
    const selectedAccessory = this.getSelectedAccessory(accessoryId);
    const stagedConfig = this.getAccessoryModalSelectionConfig();

    if (!selectedAccessory) {
      return { config: stagedConfig };
    }

    const stagedSignature = this.getAccessoryModalSelectionSignature(accessoryId, stagedConfig);
    const selectedSignature = this.getAccessoryModalSelectionSignature(accessoryId, selectedAccessory.config || {});

    if (stagedSignature === selectedSignature) {
      return selectedAccessory;
    }

    return { config: stagedConfig };
  },

  getAccessoryCustomizeModalTitle() {
    if (this.isKeyboardAccessoryCustomizeModal()) {
      const label = this.currentAccessoryButton?.label || 'Keyboard';
      return `Customize ${label}`;
    }

    const label = this.currentAccessoryButton?.label || 'Accessory';
    if (this.isModalAccessoryButton() && !this.getSelectedAccessory(this.accessoryCustomizeTarget)) {
      return `Select Your ${label}`;
    }
    return `Customize ${label}`;
  },

  getAccessoryCustomizeHeaderSubtitle() {
    const accessoryId = this.accessoryCustomizeTarget || this.accessoryModal?.accessoryId;
    if (!accessoryId) return this.currentAccessoryButton?.subtitle || '';

    if (this.isKeyboardAccessoryButton(accessoryId)) {
      return this.selectedDevice?.value || 'Choose keyboard options for your tablet kit.';
    }

    if (this.isModalAccessoryButton(accessoryId)) {
      const stagedDisplayValue = this.buildAccessoryDisplayValue(accessoryId, this.getAccessoryModalSelectionConfig());
      return stagedDisplayValue
        || this.currentAccessoryButton?.subtitle
        || 'Select your accessory details to load the right fit.';
    }

    return this.getSelectedAccessory(accessoryId)?.displayValue || this.currentAccessoryButton?.subtitle || '';
  },

  getAccessoryCustomizeEmptyStateMessage() {
    if (this.isKeyboardAccessoryCustomizeModal()) {
      return 'No keyboard options are available for this tablet configuration.';
    }

    if (this.isModalAccessoryButton()) {
      if (this.canConfirmAccessory) {
        return 'No additional options are available for this accessory. Apply to continue.';
      }
      return 'Select your accessory details to load available options.';
    }

    return 'No additional options available for this accessory.';
  },

  shouldAutoApplyModalAccessorySelection(accessoryId = this.accessoryCustomizeTarget, customizationData = null) {
    if (!accessoryId || !this.isModalAccessoryButton(accessoryId)) return false;
    if (this.modal?.type !== 'accessory-customize' || !this.modal?.open) return false;
    if (!this.canConfirmAccessory || this.accessoryCustomizeError) return false;
    if (this.hasAccessoryCustomizeOptions(accessoryId, customizationData)) return false;

    const selectedAccessory = this.getSelectedAccessory(accessoryId);
    if (!selectedAccessory) return true;

    const stagedSignature = this.getAccessoryModalSelectionSignature(accessoryId, this.getAccessoryModalSelectionConfig());
    const selectedSignature = this.getAccessoryModalSelectionSignature(accessoryId, selectedAccessory.config || {});

    return stagedSignature !== selectedSignature;
  },

  getAccessoryCustomizeApplyButtonLabel() {
    if (this.isKeyboardAccessoryCustomizeModal()) {
      return 'Apply';
    }

    if (this.isModalAccessoryButton() && !this.getSelectedAccessory(this.accessoryCustomizeTarget)) {
      return 'Select Accessory';
    }
    return 'Apply';
  },

  getKeyboardAccessoryRawOptions() {
    const field = typeof this.getKeyboardField === 'function' ? this.getKeyboardField() : null;
    if (!field || typeof this.getCustomizeFieldOptions !== 'function') return [];
    return this.getCustomizeFieldOptions('keyboard', field);
  },

  getKeyboardAccessoryOptions() {
    const options = this.getKeyboardAccessoryRawOptions();
    const noneValue = typeof this.getKeyboardNoneValue === 'function'
      ? this.getKeyboardNoneValue(options)
      : null;

    return options.filter(option => {
      if (noneValue !== null && noneValue !== undefined && option === noneValue) return false;
      return !(typeof option === 'string' && option.trim().toLowerCase().includes('none'));
    });
  },

  hasKeyboardAccessoryOptions() {
    return this.getKeyboardAccessoryOptions().length > 1;
  },

  getKeyboardAccessoryAvailableOptions() {
    const section = typeof this.getCustomizeSection === 'function'
      ? this.getCustomizeSection('keyboard')
      : null;
    const field = section?.fields?.find(item => item?.key === 'keyboard_type') || null;
    const entries = Array.isArray(section?.entries) ? section.entries : [];
    if (!field || entries.length === 0) return [];

    const seen = new Set();
    const values = [];
    entries.forEach(entry => {
      const rawValue = entry?.[field.key];
      const options = Array.isArray(rawValue) ? rawValue : [rawValue];
      options.forEach(option => {
        if (option === null || option === undefined || option === '') return;
        if (typeof option === 'string' && option.trim().toLowerCase().includes('none')) return;
        if (seen.has(option)) return;
        seen.add(option);
        values.push(option);
      });
    });

    const available = field.available || field.options;
    if (Array.isArray(available) && available.length > 0) {
      const ordered = available.filter(option => seen.has(option));
      values.forEach(option => {
        if (!ordered.includes(option)) ordered.push(option);
      });
      return ordered;
    }

    return values;
  },

  canShowKeyboardAccessoryButton() {
    if (typeof this.hasCustomizeTabletSelection === 'function' && !this.hasCustomizeTabletSelection()) {
      return false;
    }

    return this.getKeyboardAccessoryAvailableOptions().length > 0
      || !!this.context?.tablet_keyboard
      || !!this.context?.tablet?.keyboard;
  },

  getKeyboardAccessoryContext() {
    const keyboard = this.context?.tablet_keyboard || this.context?.tablet?.keyboard || null;
    return keyboard && typeof keyboard === 'object' && !Array.isArray(keyboard)
      ? keyboard
      : null;
  },

  getKeyboardAccessoryContextValue() {
    const keyboard = this.getKeyboardAccessoryContext();
    const value = keyboard?.keyboard_type;
    return value === null || value === undefined ? '' : value;
  },

  isKeyboardAccessoryContextSelected() {
    const value = this.getKeyboardAccessoryContextValue();
    if (value === '') return false;
    return !this.isKeyboardAccessoryNoneOption(value);
  },

  getKeyboardAccessoryContextDisplayValue() {
    const value = this.getKeyboardAccessoryContextValue();
    if (value === '' || this.isKeyboardAccessoryNoneOption(value)) return '';
    return this.getKeyboardAccessoryOptionLabel(value);
  },

  getKeyboardAccessoryMatchingEntry() {
    const keyboard = this.getKeyboardAccessoryContext();
    if (!keyboard) return null;

    const section = typeof this.getCustomizeSection === 'function'
      ? this.getCustomizeSection('keyboard')
      : null;
    const entries = Array.isArray(section?.entries) ? section.entries : [];
    if (entries.length === 0) return null;

    const ignoredKeys = new Set(['products', 'addons', 'images', 'image', 'alert', 'preload']);
    const selection = Object.entries(keyboard).reduce((nextSelection, [key, value]) => {
      if (ignoredKeys.has(key)) return nextSelection;
      if (value === undefined || value === null || value === '') return nextSelection;
      nextSelection[key] = value;
      return nextSelection;
    }, {});
    if (Object.keys(selection).length === 0) return null;

    const matches = entries.filter(entry => {
      if (typeof this.customizeEntryMatches === 'function') {
        return this.customizeEntryMatches(entry, selection);
      }

      return Object.entries(selection).every(([key, selectedValue]) => {
        const entryValue = entry?.[key];
        if (Array.isArray(entryValue)) return entryValue.includes(selectedValue);
        return entryValue === selectedValue;
      });
    });

    return matches.find(entry => Array.isArray(entry?.products) && entry.products.length > 0)
      || matches[0]
      || null;
  },

  getKeyboardAccessoryProductHandles() {
    const keyboard = this.getKeyboardAccessoryContext();
    if (!keyboard) return [];

    if (Array.isArray(keyboard.products) && keyboard.products.length > 0) {
      return keyboard.products.filter(handle => typeof handle === 'string' && handle.trim());
    }

    const matchingEntry = this.getKeyboardAccessoryMatchingEntry();
    return Array.isArray(matchingEntry?.products)
      ? matchingEntry.products.filter(handle => typeof handle === 'string' && handle.trim())
      : [];
  },

  getKeyboardAccessoryProductData(handle) {
    if (!handle) return null;
    if (typeof this.getCachedProductByHandle === 'function') {
      return this.getCachedProductByHandle(handle);
    }
    return this.products?.[handle] || null;
  },

  getSelectedKeyboardAccessory() {
    if (!this.isKeyboardAccessoryContextSelected()) return null;

    const accessoryId = this.getKeyboardAccessoryButtonId();
    const button = this.getAccessoryButton(accessoryId) || this.getVirtualKeyboardAccessoryButton();
    const config = this.getKeyboardAccessoryContext() || {};
    const products = this.getKeyboardAccessoryProductHandles();
    const primaryHandle = products[0] || null;
    const productData = this.getKeyboardAccessoryProductData(primaryHandle);
    const displayValue = this.buildAccessoryDisplayValue(accessoryId, config, products)
      || this.getKeyboardAccessoryContextDisplayValue();

    let error = null;
    if (!primaryHandle) {
      error = 'Keyboard product not found';
    } else if (!productData) {
      error = 'Keyboard product data not loaded';
    }

    return {
      id: accessoryId,
      config,
      displayValue,
      products,
      productData,
      loading: !!primaryHandle && !productData,
      error,
      virtual: true,
      dependencyScope: button.dependency_scope || 'device',
      dependsOn: Array.isArray(button.depends_on) ? [...button.depends_on] : []
    };
  },

  async ensureKeyboardAccessoryCustomizationReady() {
    if (typeof this.hasCustomizeTabletSelection === 'function' && !this.hasCustomizeTabletSelection()) {
      return false;
    }

    if (typeof this.hasValidCustomizeTabletSelection === 'function' && !this.hasValidCustomizeTabletSelection()) {
      return false;
    }

    if (this.getKeyboardAccessoryAvailableOptions().length > 0) {
      return true;
    }

    if (typeof this.requestCustomizeDataForCurrentTablet !== 'function') {
      return false;
    }

    const previousTarget = this.customizeTarget;
    this.customizeTarget = 'tablet';

    try {
      await this.requestCustomizeDataForCurrentTablet({
        allowedModalTypes: [null, 'customize', 'accessory-customize']
      });
      return this.getKeyboardAccessoryAvailableOptions().length > 0;
    } catch (err) {
      console.warn('[Tough-Bar Accessories] Failed to hydrate tablet keyboard customization:', err);
      return false;
    } finally {
      if (!this.modal?.open && previousTarget) {
        this.customizeTarget = previousTarget;
      }
    }
  },

  isKeyboardAccessoryNoneOption(option) {
    const options = this.getKeyboardAccessoryRawOptions();
    const noneValue = typeof this.getKeyboardNoneValue === 'function'
      ? this.getKeyboardNoneValue(options)
      : null;

    if (noneValue !== null && noneValue !== undefined) {
      return option === noneValue;
    }

    return typeof option === 'string' && option.trim().toLowerCase().includes('none');
  },

  getKeyboardAccessoryOptionLabel(option) {
    if (this.isKeyboardAccessoryNoneOption(option)) return 'No keyboard';
    if (typeof this.getCustomizeOptionLabel === 'function') {
      return this.getCustomizeOptionLabel('keyboard_type', option);
    }
    return option;
  },

  isKeyboardAccessoryOptionSelected(option) {
    return this.getCustomizeSelectionValue('keyboard', 'keyboard_type') === option;
  },

  updateKeyboardAccessoryOption(option) {
    const field = typeof this.getKeyboardField === 'function' ? this.getKeyboardField() : null;
    if (!field || typeof this.updateCustomizeSelection !== 'function') return;
    this.updateCustomizeSelection('keyboard', field.key, option, field.type || 'select');
  },

  isKeyboardAccessoryNoKeyboardSelected() {
    const field = typeof this.getKeyboardField === 'function' ? this.getKeyboardField() : null;
    if (!field) return true;

    const value = this.getCustomizeSelectionValue('keyboard', field.key);
    if (value === '') return true;

    return !this.getKeyboardAccessoryOptions().includes(value);
  },

  clearKeyboardAccessorySelection(accessoryId) {
    const index = this.selectedAccessories.findIndex(accessory => {
      if (!accessory?.id) return false;
      return this.isAccessoryIdMatch(accessory.id, accessoryId)
        || this.isKeyboardAccessoryButton(accessory.id);
    });
    if (index === -1) return false;

    const removedId = this.selectedAccessories[index]?.id || accessoryId;
    this.invalidateAccessoryRequests(removedId);
    this.selectedAccessories.splice(index, 1);
    this.clearAccessoryCustomizeAvailability(removedId);
    this.requestSideCartPositionRecalc();
    return true;
  },

  clearKeyboardAccessoryContext() {
    if (typeof this.clearTabletKeyboardContext === 'function') {
      this.clearTabletKeyboardContext();
      return;
    }

    const nextContext = { ...(this.context || {}) };
    delete nextContext.tablet_keyboard;

    if (nextContext.tablet && typeof nextContext.tablet === 'object' && !Array.isArray(nextContext.tablet)) {
      const tablet = { ...nextContext.tablet };
      delete tablet.keyboard;
      nextContext.tablet = tablet;
    }

    this.context = nextContext;
  },

  getKeyboardlessMountConfigurationOption(currentConfiguration = null) {
    const currentValue = currentConfiguration
      ?? this.customizeSelections?.mount?.configuration
      ?? this.context?.tablet_mount?.configuration
      ?? null;
    const currentLabel = String(currentValue || '').trim();
    if (!currentLabel) return null;

    const stripKeyboard = value => String(value || '')
      .replace(/\s*\+\s*keyboard\b/ig, '')
      .replace(/\s+with\s+keyboard\b/ig, '')
      .replace(/\s+keyboard\b/ig, '')
      .replace(/\s+/g, ' ')
      .trim();
    const includesKeyboard = value => /\bkeyboard\b/i.test(String(value || ''));
    const targetLabel = stripKeyboard(currentLabel);
    const configField = typeof this.getCustomizeConfigField === 'function'
      ? this.getCustomizeConfigField()
      : null;
    const options = configField?.field && typeof this.getCustomizeFieldOptions === 'function'
      ? this.getCustomizeFieldOptions(configField.sectionId, configField.field)
      : [];
    const keyboardlessOptions = options.filter(option => !includesKeyboard(option));
    const directMatch = keyboardlessOptions.find(option => {
      return stripKeyboard(option).toLowerCase() === targetLabel.toLowerCase();
    });

    if (directMatch) return directMatch;

    const currentLower = currentLabel.toLowerCase();
    const familyMatch = ['centered', 'offset'].find(token => currentLower.includes(token));
    if (familyMatch) {
      const optionMatch = keyboardlessOptions.find(option => {
        const normalized = String(option || '').toLowerCase();
        return normalized.includes(familyMatch);
      });
      if (optionMatch) return optionMatch;
    }

    return includesKeyboard(currentLabel) && targetLabel ? targetLabel : null;
  },

  stageKeyboardAccessoryRemovalSelections() {
    if (!this.customization?.sections) return false;

    if (typeof this.initCustomizeSelections === 'function') {
      this.initCustomizeSelections();
    }

    const nextSelections = { ...(this.customizeSelections || {}) };
    const configField = typeof this.getCustomizeConfigField === 'function'
      ? this.getCustomizeConfigField()
      : null;
    const nextMountConfiguration = this.getKeyboardlessMountConfigurationOption();
    let staged = false;

    if (configField?.field?.key && nextMountConfiguration) {
      nextSelections.mount = {
        ...(nextSelections.mount || {}),
        [configField.field.key]: nextMountConfiguration
      };
      staged = true;
    }

    const keyboardField = typeof this.getKeyboardField === 'function' ? this.getKeyboardField() : null;
    if (keyboardField?.key) {
      const options = typeof this.getCustomizeFieldOptions === 'function'
        ? this.getCustomizeFieldOptions('keyboard', keyboardField, nextSelections)
        : [];
      const noneValue = typeof this.getKeyboardNoneValue === 'function'
        ? this.getKeyboardNoneValue(options)
        : null;

      nextSelections.keyboard = {
        ...(nextSelections.keyboard || {}),
        [keyboardField.key]: noneValue ?? null
      };
      staged = true;
    }

    if (!staged) return false;

    this.customizeSelections = nextSelections;
    if (typeof this.syncAllCustomizeSelections === 'function') {
      this.syncAllCustomizeSelections();
    }
    if (typeof this.updateCustomizePrices === 'function') {
      this.updateCustomizePrices();
    }

    return true;
  },

  async applyKeyboardAccessoryRemoval(accessoryId) {
    const resolvedAccessoryId = accessoryId || this.getKeyboardAccessoryButtonId();
    const previousTarget = this.customizeTarget;
    this.customizeTarget = 'tablet';
    this.accessoryCustomizeError = null;
    this.customizeError = null;

    try {
      if (!this.customization?.sections && typeof this.ensureKeyboardAccessoryCustomizationReady === 'function') {
        await this.ensureKeyboardAccessoryCustomizationReady();
      }

      if (!this.stageKeyboardAccessoryRemovalSelections()) {
        this.customizeError = 'Failed to find a no-keyboard tablet configuration.';
        return false;
      }

      if (typeof this.applyCustomization !== 'function') {
        this.customizeError = 'Tablet customization is unavailable.';
        return false;
      }

      await this.applyCustomization();
      if (this.customizeError) return false;

      this.clearKeyboardAccessoryContext();
      this.clearKeyboardAccessorySelection(resolvedAccessoryId);

      if (typeof this.resetCustomizeSessionState === 'function') {
        this.resetCustomizeSessionState();
      }

      return true;
    } catch (err) {
      console.error('[Tough-Bar Accessories] Failed to remove keyboard accessory:', err);
      this.customizeError = err.message || 'Failed to remove keyboard accessory.';
      return false;
    } finally {
      if (!this.modal?.open && previousTarget) {
        this.customizeTarget = previousTarget;
      }
    }
  },

  async resetKeyboardAccessorySelections() {
    this.accessoryCustomizeError = null;
    this.customizeError = null;
    await this.resetCustomizeSelections();
  },

  canApplyKeyboardAccessoryCustomization() {
    if (this.accessoryCustomizeLoading || this.customizeLoading) return false;
    if (!this.accessoryCustomizeTarget) return false;
    if (!this.customization) return false;
    if (typeof this.canApplyCustomizeModal !== 'function') return false;
    return this.canApplyCustomizeModal();
  },

  async applyKeyboardAccessoryCustomization() {
    if (!this.canApplyKeyboardAccessoryCustomization()) return;

    const accessoryId = this.accessoryCustomizeTarget || this.modal?.accessoryId;
    if (!accessoryId) return;
    const shouldClearKeyboardContext = this.isKeyboardAccessoryNoKeyboardSelected();

    this.accessoryCustomizeLoading = true;
    this.accessoryCustomizeError = null;
    this.customizeError = null;
    this.customizeTarget = 'tablet';
    this.customizeApplied = false;

    try {
      await this.applyCustomization();

      const applied = !this.customizeError && !this.modal?.open;
      if (!applied) {
        this.accessoryCustomizeError = this.customizeError || 'Failed to apply keyboard selection.';
        return;
      }

      if (shouldClearKeyboardContext) {
        this.clearKeyboardAccessoryContext();
      }
      this.clearKeyboardAccessorySelection(accessoryId);
      this.resetCustomizeSessionState();
      this.scheduleAccessorySnapshotRefresh();
    } catch (err) {
      console.error('[Tough-Bar Accessories] Failed to apply keyboard accessory customization:', err);
      this.accessoryCustomizeError = err.message || 'Failed to apply keyboard selection.';
    } finally {
      this.accessoryCustomizeLoading = false;
    }
  },

  async openKeyboardAccessoryCustomizeModal(accessoryId) {
    const button = this.getAccessoryButton(accessoryId);
    if (!button) return;

    const preferredSelection = this.getPreferredCustomizeTabletSelection();
    this.applyTabletSelection(preferredSelection);

    if (!this.hasCustomizeTabletSelection()) {
      this.error = 'Select a tablet before choosing a keyboard.';
      return;
    }

    if (!this.hasValidCustomizeTabletSelection()) {
      this.error = 'Select a valid tablet case type before choosing a keyboard.';
      return;
    }

    this.customizeTarget = 'tablet';
    this.customizeError = null;
    this.clearCustomizeDataState();
    this.snapshotCustomizeSessionState();

    this.accessoryCustomizeLoading = true;
    this.accessoryCustomizeError = null;
    this.accessoryCustomization = null;
    this.accessoryCustomizeSelections = {};
    this.accessoryCustomizeTarget = accessoryId;
    this.accessoryCustomizeSignature = null;
    this.accessoryCustomizePrimarySelectionBackup = null;
    this.accessoryModal = this.buildAccessoryModalState(accessoryId);
    this.openModal('accessory-customize', { accessoryId });

    const requestToken = this.nextAccessoryRequestToken('customize-modal', accessoryId);

    try {
      const shouldForceTabletOptionsHydration = Boolean(this.currentSharedConfigId || this.sharedConfigIdFromUrl);
      await Promise.all([
        this.ensureTabletOptionsCache({ force: shouldForceTabletOptionsHydration }),
        this.refreshCustomizeForTabletSelection({
          force: true,
          allowedModalTypes: ['accessory-customize']
        })
      ]);

      if (!this.isAccessoryRequestTokenActive(requestToken) || !this.isKeyboardAccessoryCustomizeModal()) return;
      if (this.customizeError) {
        throw new Error(this.customizeError);
      }
    } catch (err) {
      if (!this.isAccessoryRequestTokenActive(requestToken)) return;
      console.error('[Tough-Bar Accessories] Failed to load keyboard accessory customization:', err);
      this.accessoryCustomizeError = err.message || 'Failed to load keyboard options.';
    } finally {
      if (this.isAccessoryRequestTokenActive(requestToken)) {
        this.accessoryCustomizeLoading = false;
      }
    }
  },

  /**
   * Determine whether an accessory error should block Step 3 progression.
   * @param {object} accessory - Selected accessory state object
   * @returns {boolean}
   */
  isAccessoryErrorBlocking(accessory) {
    if (!accessory) return false;
    return !!accessory.error;
  },

  resetAccessoryCustomizeState() {
    this.accessoryCustomization = null;
    this.accessoryCustomizeSelections = {};
    this.accessoryCustomizeLoading = false;
    this.accessoryCustomizeError = null;
    this.accessoryCustomizeTarget = null;
    this.accessoryCustomizeSignature = null;
    this.accessoryCustomizePrimarySelectionBackup = null;
    this.accessoryModal = this.buildAccessoryModalState(null);
  },

  normalizeAccessoryCustomizeValue(value, fieldType = 'select') {
    if (value === undefined || value === null) return null;

    if (fieldType === 'toggle') {
      if (value === true || value === 'true' || value === 'Yes' || value === 'yes') return true;
      if (value === false || value === 'false' || value === 'No' || value === 'no') return false;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    }

    return value;
  },

  accessoryCustomizeValuesEqual(left, right) {
    const leftEmpty = left === undefined || left === null || left === '';
    const rightEmpty = right === undefined || right === null || right === '';
    if (leftEmpty && rightEmpty) return true;
    return left === right;
  },

  getAccessoryCustomizeField(fieldKey) {
    return this.getAccessoryCustomizeFields().find(field => field.key === fieldKey) || null;
  },

  getAccessoryCustomizeFields() {
    const primaryKeys = new Set(this.getAccessoryCustomizePrimaryKeys(this.accessoryCustomizeTarget));
    const apiFields = Array.isArray(this.accessoryCustomization?.fields)
      ? this.accessoryCustomization.fields.filter(field => !primaryKeys.has(field?.key))
      : [];
    const entries = this.getAccessoryCustomizeEntries();

    if (entries.length === 0) {
      return apiFields;
    }

    const hasMountTypeEntry = entries.some(entry => {
      return this.normalizeAccessoryCustomizeValue(entry?.mount_type, 'select') !== null;
    });

    const hasMountTypeField = apiFields.some(field => field?.key === 'mount_type');
    if (!hasMountTypeEntry || hasMountTypeField) {
      return apiFields;
    }

    return [
      { key: 'mount_type', label: 'Mount Type', type: 'select' },
      ...apiFields
    ];
  },

  getAccessoryCustomizeEntries() {
    return Array.isArray(this.accessoryCustomization?.entries) ? this.accessoryCustomization.entries : [];
  },

  getAccessoryCustomizeFilteredEntries(selectionOverrides = {}, excludeFieldKey = null) {
    const fields = this.getAccessoryCustomizeFields();
    const entries = this.getAccessoryCustomizeEntries();
    const selections = { ...(this.accessoryCustomizeSelections || {}), ...(selectionOverrides || {}) };

    if (fields.length === 0 || entries.length === 0) {
      return entries;
    }

    return entries.filter(entry => {
      return fields.every(field => {
        if (!field?.key || field.key === excludeFieldKey) return true;

        const selectedValue = this.normalizeAccessoryCustomizeValue(selections[field.key], field.type);
        if (selectedValue === null) return true;

        const entryValue = this.normalizeAccessoryCustomizeValue(entry?.[field.key], field.type);
        return this.accessoryCustomizeValuesEqual(entryValue, selectedValue);
      });
    });
  },

  getAccessoryCustomizeFieldOptions(fieldOrKey, selectionOverrides = null) {
    const field = typeof fieldOrKey === 'string' ? this.getAccessoryCustomizeField(fieldOrKey) : fieldOrKey;
    if (!field?.key) return [];

    const allEntries = this.getAccessoryCustomizeEntries();

    // Keep mount_type visible like tablet configuration: do not filter it by
    // dependent selections (holder/base), let dependent fields react instead.
    const entries = field.key === 'mount_type'
      ? allEntries
      : this.getAccessoryCustomizeFilteredEntries(selectionOverrides || {}, field.key);
    const values = [];

    entries.forEach(entry => {
      const value = this.normalizeAccessoryCustomizeValue(entry?.[field.key], field.type);
      if (value === null) return;
      if (!values.some(existing => this.accessoryCustomizeValuesEqual(existing, value))) {
        values.push(value);
      }
    });

    // Only fall back to API-provided static options when no entries are present.
    // If entries exist and current filtering yields no values (e.g., MagSafe),
    // return [] so dependent fields hide correctly.
    if (values.length === 0 && Array.isArray(field.options) && allEntries.length === 0) {
      field.options.forEach(option => {
        const value = this.normalizeAccessoryCustomizeValue(option, field.type);
        if (value === null) return;
        if (!values.some(existing => this.accessoryCustomizeValuesEqual(existing, value))) {
          values.push(value);
        }
      });
    }

    return values;
  },

  getAccessoryCustomizeSelectionValue(fieldKey) {
    const field = this.getAccessoryCustomizeField(fieldKey);
    const value = this.normalizeAccessoryCustomizeValue(
      this.accessoryCustomizeSelections?.[fieldKey],
      field?.type || 'select'
    );

    if (value === null) return '';
    if (value === true) return 'true';
    if (value === false) return 'false';
    return value;
  },

  getAccessoryCustomizeOptionLabel(option) {
    if (option === true) return 'Yes';
    if (option === false) return 'No';
    return option;
  },

  isAccessoryCustomizeOptionSelected(fieldKey, option) {
    const field = this.getAccessoryCustomizeField(fieldKey);
    const currentValue = this.normalizeAccessoryCustomizeValue(
      this.accessoryCustomizeSelections?.[fieldKey],
      field?.type || 'select'
    );
    const optionValue = this.normalizeAccessoryCustomizeValue(option, field?.type || 'select');
    return this.accessoryCustomizeValuesEqual(currentValue, optionValue);
  },

  getAccessoryCustomizeOptionImage(fieldKey, option) {
    const candidates = this.getAccessoryCustomizeImageCandidates(fieldKey, option);
    return candidates[0] || null;
  },

  getAccessoryCustomizeImageCandidates(fieldKey, option) {
    const base = 'https://images.rammount.com/images/builders/tough-bar/Customize-Modal/';

    if (option === undefined || option === null) return [];

    const raw = String(option).replace(/\\"/g, '"').trim();
    if (!raw) return [];

    const normalized = raw.replace(/\s+/g, ' ').trim();
    const noMarks = normalized.replace(/[™®]/g, '').replace(/\s+/g, ' ').trim();
    const noPunctuation = noMarks.replace(/[’']/g, '').replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
    const withHyphens = noPunctuation.replace(/\s+/g, '-');

    const preferred = [];
    const mapped = [];
    if (fieldKey === 'base_type') {
      if (normalized.toLowerCase().includes('tough-claw')) mapped.push('Tough-Claw');
      if (normalized.toLowerCase().includes('torque')) mapped.push('Torque');
    }

    if (fieldKey === 'holder_type') {
      if (normalized.toLowerCase().includes('x-grip')) {
        if ((this.accessoryCustomizeTarget || '').toLowerCase() === 'phone') {
          preferred.push('X-Grip® (Phone)');
        }
        mapped.push('X-Grip');
      }
      if (normalized.toLowerCase().includes('quick-grip') && normalized.toLowerCase().includes('pro') && normalized.toLowerCase().includes('xl')) {
        mapped.push('Quick-Grip Pro XL');
      }
      if (normalized.toLowerCase().includes('quick-grip') && normalized.toLowerCase().includes('pro') && !normalized.toLowerCase().includes('xl')) {
        mapped.push('Quick-Grip Pro');
      }
      if (normalized.toLowerCase().includes('quick-grip') && normalized.toLowerCase().includes('xl') && !normalized.toLowerCase().includes('pro')) {
        mapped.push('Quick-Grip XL');
      }
    }

    const names = [];
    [...preferred, normalized, noMarks, noPunctuation, withHyphens, ...mapped].forEach(value => {
      if (!value) return;
      if (!names.includes(value)) names.push(value);
    });

    return names.map(name => `${base}${encodeURI(name)}.jpg`);
  },

  handleAccessoryCustomizeImageError(event, fieldKey, option) {
    const target = event?.target;
    if (!target) return;

    const candidates = this.getAccessoryCustomizeImageCandidates(fieldKey, option);
    if (candidates.length === 0) return;

    const currentSrc = target.getAttribute('src') || '';
    const currentIndex = candidates.indexOf(currentSrc);
    const nextIndex = currentIndex === -1 ? 1 : currentIndex + 1;

    if (nextIndex < candidates.length) {
      target.setAttribute('src', candidates[nextIndex]);
      return;
    }

    target.style.display = 'none';
  },

  getAccessoryCustomizeSectionLabel() {
    const target = (this.accessoryCustomizeTarget || '').toLowerCase();
    if (target === 'phone') return 'Phone Mount';

    const baseLabel = this.currentAccessoryButton?.label || 'Accessory';
    if (baseLabel.toLowerCase().includes('mount')) return baseLabel;
    return `${baseLabel} Mount`;
  },

  syncAccessoryCustomizeSelections() {
    const fields = this.getAccessoryCustomizeFields();
    if (fields.length === 0) return;

    let selections = { ...(this.accessoryCustomizeSelections || {}) };

    for (let pass = 0; pass < 3; pass++) {
      let changed = false;

      fields.forEach(field => {
        if (!field?.key) return;

        const options = this.getAccessoryCustomizeFieldOptions(field, selections);
        const currentValue = this.normalizeAccessoryCustomizeValue(selections[field.key], field.type);

        if (options.length === 0) {
          if (currentValue !== null) {
            selections[field.key] = null;
            changed = true;
          }
          return;
        }

        const currentValid = options.some(option => this.accessoryCustomizeValuesEqual(option, currentValue));
        if (currentValid) return;

        const preloadValue = this.normalizeAccessoryCustomizeValue(
          this.accessoryCustomization?.preload?.[field.key],
          field.type
        );

        if (preloadValue !== null && options.some(option => this.accessoryCustomizeValuesEqual(option, preloadValue))) {
          selections[field.key] = preloadValue;
        } else {
          selections[field.key] = options[0];
        }
        changed = true;
      });

      this.accessoryCustomizeSelections = { ...selections };
      selections = { ...this.accessoryCustomizeSelections };
      if (!changed) break;
    }
  },

  initAccessoryCustomizeSelections(accessory) {
    const fields = this.getAccessoryCustomizeFields();
    const preload = this.accessoryCustomization?.preload || {};
    const config = accessory?.config || {};
    const selections = {};

    fields.forEach(field => {
      if (!field?.key) return;
      let value = config[field.key];
      if (value === undefined) value = preload[field.key];
      selections[field.key] = this.normalizeAccessoryCustomizeValue(value, field.type);
    });

    this.accessoryCustomizeSelections = selections;
    this.syncAccessoryCustomizeSelections();
    this.fetchAccessoryCustomizeProducts();
  },

  async resetAccessoryCustomizeSelections() {
    this.accessoryCustomizeError = null;

    if (this.isKeyboardAccessoryCustomizeModal()) {
      await this.resetKeyboardAccessorySelections();
      return;
    }

    if (this.isModalAccessoryButton()) {
      const accessoryId = this.accessoryCustomizeTarget || this.accessoryModal?.accessoryId;
      const backup = this.accessoryCustomizePrimarySelectionBackup
        || this.buildAccessoryModalState(accessoryId);

      this.accessoryModal = {
        ...backup,
        accessoryId: accessoryId || backup.accessoryId || null
      };

      await this.refreshAccessoryCustomizeForModalSelection({ force: true });
      return;
    }

    const fields = this.getAccessoryCustomizeFields();
    if (fields.length === 0) return;

    const preload = this.accessoryCustomization?.preload || {};
    const selections = {};

    fields.forEach(field => {
      if (!field?.key) return;
      selections[field.key] = this.normalizeAccessoryCustomizeValue(preload[field.key], field.type);
    });

    this.accessoryCustomizeSelections = selections;
    this.syncAccessoryCustomizeSelections();
    this.fetchAccessoryCustomizeProducts();
  },

  getAccessoryCustomizeMatchingEntry() {
    const entries = this.getAccessoryCustomizeFilteredEntries();
    if (entries.length === 0) return null;

    const preloadEntry = entries.find(entry => entry?.preload === true);
    return preloadEntry || entries[0];
  },

  getAccessoryCustomizeSectionPrice() {
    const entry = this.getAccessoryCustomizeMatchingEntry();
    if (!entry) return null;

    let total = 0;
    let hasPrice = false;
    const handles = entry.products || [];

    handles.forEach(handle => {
      const product = this.products?.[handle];
      if (!product?.price || product?.available === false) return;
      total += product.price;
      hasPrice = true;
    });

    return hasPrice ? total : null;
  },

  getAccessoryCustomizeSubtotal() {
    if (this.isKeyboardAccessoryCustomizeModal()) return null;
    return this.getAccessoryCustomizeSectionPrice();
  },

  async refreshAccessoryCustomizeForModalSelection({ force = false } = {}) {
    const accessoryId = this.accessoryCustomizeTarget || this.accessoryModal?.accessoryId;
    if (!accessoryId || !this.isModalAccessoryButton(accessoryId)) return;

    this.accessoryCustomizeError = null;

    if (!this.canConfirmAccessory) {
      this.nextAccessoryRequestToken('customize-modal', accessoryId);
      this.accessoryCustomization = null;
      this.accessoryCustomizeSelections = {};
      this.accessoryCustomizeSignature = null;
      this.accessoryCustomizeLoading = false;
      return;
    }

    const selectionSignature = this.getAccessoryModalSelectionSignature(accessoryId);
    if (!force && this.accessoryCustomization && this.accessoryCustomizeSignature === selectionSignature) {
      return;
    }

    this.accessoryCustomization = null;
    this.accessoryCustomizeSelections = {};
    this.accessoryCustomizeSignature = null;
    this.accessoryCustomizeLoading = true;

    const requestToken = this.nextAccessoryRequestToken('customize-modal', accessoryId);
    const config = this.getAccessoryModalSelectionConfig();

    try {
      const body = this.buildAccessoryCustomizeRequestBody(accessoryId, config);
      const data = await this.postToBuilder(this.getBuilderEndpoint(), body);
      if (data?.error) {
        throw new Error(data.error);
      }

      if (!this.isAccessoryRequestTokenActive(requestToken)) return;
      if (this.modal?.type !== 'accessory-customize' || !this.modal?.open) return;
      if (!this.isAccessoryIdMatch(this.accessoryCustomizeTarget, accessoryId)) return;
      if (this.getAccessoryModalSelectionSignature(accessoryId) !== selectionSignature) return;

      this.accessoryCustomization = this.normalizeAccessoryCustomizationPayload(data);
      this.accessoryCustomizeSignature = selectionSignature;
      this.initAccessoryCustomizeSelections(this.getAccessoryCustomizeSelectionSeed(accessoryId));

      if (this.shouldAutoApplyModalAccessorySelection(accessoryId, this.accessoryCustomization)) {
        this.accessoryCustomizeLoading = false;
        await this.applyAccessoryCustomization();
        return;
      }
    } catch (err) {
      if (!this.isAccessoryRequestTokenActive(requestToken)) return;
      if (!this.isAccessoryIdMatch(this.accessoryCustomizeTarget, accessoryId)) return;
      console.error('[Tough-Bar Accessories] Failed to load modal accessory customization:', err);
      this.accessoryCustomizeError = err.message || 'Failed to load accessory customization.';
    } finally {
      if (
        this.isAccessoryRequestTokenActive(requestToken)
        && this.isAccessoryIdMatch(this.accessoryCustomizeTarget, accessoryId)
      ) {
        this.accessoryCustomizeLoading = false;
      }
    }
  },

  async fetchAccessoryCustomizeProducts() {
    if (!this.accessoryCustomization) return;

    const entry = this.getAccessoryCustomizeMatchingEntry();
    if (!entry) return;

    const handles = entry.products || [];
    const fetchPromises = [];

    handles.forEach(handle => {
      if (handle && !this.products?.[handle]) {
        fetchPromises.push(this.fetchProductData(handle));
      }
    });

    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
      this.products = { ...this.products };
    }
  },

  canApplyAccessoryCustomization() {
    if (this.isKeyboardAccessoryCustomizeModal()) {
      return this.canApplyKeyboardAccessoryCustomization();
    }

    if (this.accessoryCustomizeLoading) return false;
    if (!this.accessoryCustomizeTarget) return false;

    const isModalAccessory = this.isModalAccessoryButton(this.accessoryCustomizeTarget);
    if (!isModalAccessory) {
      if (!this.accessoryCustomization) return false;
      if (!this.getSelectedAccessory(this.accessoryCustomizeTarget)) return false;
    } else if (!this.canConfirmAccessory) {
      return false;
    }

    const fields = this.getAccessoryCustomizeFields();
    if (fields.length === 0) return isModalAccessory ? this.canConfirmAccessory : false;

    return fields.every(field => {
      if (!field?.key) return true;

      const options = this.getAccessoryCustomizeFieldOptions(field);
      if (options.length <= 1) return true;

      const value = this.normalizeAccessoryCustomizeValue(
        this.accessoryCustomizeSelections?.[field.key],
        field.type
      );
      return options.some(option => this.accessoryCustomizeValuesEqual(option, value));
    });
  },

  updateAccessoryCustomizeSelection(fieldKey, rawValue, fieldType = 'select') {
    const value = this.normalizeAccessoryCustomizeValue(rawValue, fieldType);
    this.accessoryCustomizeSelections = {
      ...(this.accessoryCustomizeSelections || {}),
      [fieldKey]: value
    };
    this.accessoryCustomizeError = null;
    this.syncAccessoryCustomizeSelections();
    this.fetchAccessoryCustomizeProducts();
  },

  buildAccessoryCustomizeRequestBody(accessoryId, accessoryConfig = {}) {
    const selectedAccessory = this.getSelectedAccessory(accessoryId);
    const resolvedAccessoryId = selectedAccessory?.id || accessoryId;
    const body = this.buildApiContext({ accessory: resolvedAccessoryId });
    const config = accessoryConfig && typeof accessoryConfig === 'object' ? accessoryConfig : {};
    const nextAccessories = body.accessories && typeof body.accessories === 'object'
      ? { ...body.accessories }
      : {};
    const existingKey = Object.keys(nextAccessories).find(key => this.isAccessoryIdMatch(key, resolvedAccessoryId));
    const targetKey = existingKey || resolvedAccessoryId;
    const existingConfig = nextAccessories[targetKey] && typeof nextAccessories[targetKey] === 'object'
      ? nextAccessories[targetKey]
      : {};
    const mergedConfig = {
      ...existingConfig,
      ...config
    };
    const isModalAccessory = this.isModalAccessoryButton(resolvedAccessoryId);
    const primaryKeys = isModalAccessory
      ? new Set(this.getAccessoryCustomizePrimaryKeys(resolvedAccessoryId))
      : null;
    const requestConfig = isModalAccessory
      ? Object.fromEntries(
          Object.entries(mergedConfig).filter(([key, value]) => {
            if (!primaryKeys.has(key)) return false;
            const normalized = this.normalizeAccessoryCustomizeValue(value, 'select');
            return normalized !== null;
          })
        )
      : mergedConfig;

    if (Object.keys(requestConfig).length > 0) {
      nextAccessories[targetKey] = requestConfig;
      body.accessories = nextAccessories;
    }

    return body;
  },

  buildConfigurableAccessoryCustomization(button, accessory = null) {
    const options = Array.isArray(button?.options) ? button.options : [];
    if (options.length === 0) return null;

    const ignoredKeys = new Set(['products', 'addons', 'images', 'image', 'alert', 'preload']);
    const labelMap = {
      mount_type: 'Mount Type',
      holder_type: 'Holder Type',
      base_type: 'Base Type'
    };
    const fieldKeys = [];

    options.forEach(option => {
      Object.keys(option || {}).forEach(key => {
        if (ignoredKeys.has(key)) return;
        if (!fieldKeys.includes(key)) fieldKeys.push(key);
      });
    });

    const selectedHandle = accessory?.products?.[0];
    const preloadHandle = button?.preload?.products?.[0];

    let preloadIndex = -1;
    if (selectedHandle) {
      preloadIndex = options.findIndex(option => (option?.products || [])[0] === selectedHandle);
    }
    if (preloadIndex === -1 && preloadHandle) {
      preloadIndex = options.findIndex(option => (option?.products || [])[0] === preloadHandle);
    }
    if (preloadIndex === -1) preloadIndex = 0;

    const fields = fieldKeys.map(key => ({
      key,
      label: labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
      type: 'select'
    }));

    const entries = options.map((option, index) => {
      const entry = {
        products: Array.isArray(option?.products) ? option.products : []
      };
      fieldKeys.forEach(key => {
        const value = option?.[key];
        if (value === undefined || value === null || value === '') return;
        entry[key] = value;
      });
      if (index === preloadIndex) {
        entry.preload = true;
      }
      return entry;
    });

    const preload = {};
    const preloadSource = options[preloadIndex] || button?.preload || {};
    fieldKeys.forEach(key => {
      const value = preloadSource?.[key];
      if (value === undefined || value === null || value === '') return;
      preload[key] = value;
    });

    return {
      fields,
      entries,
      preload
    };
  },

  async applyAccessoryCustomization() {
    if (this.isKeyboardAccessoryCustomizeModal()) {
      await this.applyKeyboardAccessoryCustomization();
      return;
    }

    if (!this.canApplyAccessoryCustomization()) return;

    const accessoryId = this.accessoryCustomizeTarget;
    let accessory = this.getSelectedAccessory(accessoryId);
    const button = this.getAccessoryButton(accessoryId);
    const isModalAccessory = button?.type === 'modal';

    this.accessoryCustomizeLoading = true;
    this.accessoryCustomizeError = null;

    try {
      const matchingEntry = this.getAccessoryCustomizeMatchingEntry();
      const fields = this.getAccessoryCustomizeFields();
      if (!matchingEntry && (!isModalAccessory || fields.length > 0)) {
        throw new Error('No matching accessory configuration found.');
      }

      const nextConfig = isModalAccessory
        ? this.getAccessoryModalSelectionConfig()
        : { ...(accessory?.config || {}) };
      fields.forEach(field => {
        if (!field?.key) return;
        const value = this.normalizeAccessoryCustomizeValue(
          this.accessoryCustomizeSelections?.[field.key],
          field.type
        );
        if (value === null) {
          delete nextConfig[field.key];
          return;
        }
        nextConfig[field.key] = value;
      });

      const nextProducts = matchingEntry?.products || accessory?.products || [];
      if (!isModalAccessory) {
        nextConfig.products = nextProducts;
      }

      if (isModalAccessory) {
        const dependencyMeta = this.buildAccessoryDependencyMeta(accessoryId);
        const nextDisplayValue = this.buildAccessoryDisplayValue(accessoryId, nextConfig, nextProducts);
        if (!accessory) {
          this.selectedAccessories.push({
            id: accessoryId,
            config: nextConfig,
            displayValue: nextDisplayValue,
            products: nextProducts,
            productData: null,
            loading: true,
            error: null,
            dependencyScope: dependencyMeta.dependencyScope,
            dependsOn: dependencyMeta.dependsOn
          });
          this.requestSideCartPositionRecalc();
        } else {
          this.replaceSelectedAccessory(accessoryId, {
            config: nextConfig,
            displayValue: nextDisplayValue,
            products: nextProducts,
            productData: null,
            loading: true,
            error: null,
            dependencyScope: dependencyMeta.dependencyScope,
            dependsOn: dependencyMeta.dependsOn
          });
        }

        const hasCustomizeOptions = this.accessoryCustomization
          ? this.hasAccessoryCustomizeOptions(accessoryId, this.accessoryCustomization)
          : true;
        this.accessoryCustomizeAvailability = {
          ...(this.accessoryCustomizeAvailability || {}),
          [accessoryId]: hasCustomizeOptions
        };

        await this.fetchModalAccessoryProduct(accessoryId, nextConfig);
      } else {
        await this.updateAccessoryConfig(accessoryId, {
          ...nextConfig,
          products: nextProducts
        });
      }

      const updated = this.getSelectedAccessory(accessoryId);
      if (!updated || updated.error || !updated.productData) {
        throw new Error(updated?.error || 'No product found for this configuration.');
      }

      this.closeModal();
      this.scheduleAccessorySnapshotRefresh();
    } catch (err) {
      console.error('[Tough-Bar Accessories] Failed to apply accessory customization:', err);
      this.accessoryCustomizeError = err.message || 'Failed to apply accessory customization.';
    } finally {
      this.accessoryCustomizeLoading = false;
    }
  },

  /**
   * Open accessory modal for modal-type accessories
   * @param {string} accessoryId - The accessory button ID
   */
  async openAccessoryModal(accessoryId) {
    await this.openAccessoryCustomizeModal(accessoryId);
  },

  /**
   * Select make in accessory modal
   * @param {string} make - The selected make
   */
  selectAccessoryMake(make) {
    this.accessoryModal.make = make;
    this.accessoryModal.model = '';
    this.accessoryModal.case_type = '';
  },

  /**
   * Select model in accessory modal
   * @param {string} model - The selected model
   */
  selectAccessoryModel(model) {
    this.accessoryModal.model = model;
    this.accessoryModal.case_type = '';
  },

  async updateAccessoryCustomizeMake(make) {
    this.selectAccessoryMake(make);
    await this.refreshAccessoryCustomizeForModalSelection();
  },

  async updateAccessoryCustomizeModel(model) {
    this.selectAccessoryModel(model);
    await this.refreshAccessoryCustomizeForModalSelection();
  },

  async updateAccessoryCustomizeCaseType(caseType) {
    this.accessoryModal.case_type = caseType;
    await this.refreshAccessoryCustomizeForModalSelection();
  },

  /**
   * Confirm accessory modal selection
   * Makes API call to get product data for the selected accessory
   */
  async confirmAccessorySelection() {
    if (!this.canConfirmAccessory) return;

    const accessoryId = this.accessoryModal.accessoryId;
    const button = this.getAccessoryButton(accessoryId);
    if (!button) return;

    // Build config object
    const config = {
      make: this.accessoryModal.make,
      model: this.accessoryModal.model
    };
    if (this.accessoryModal.case_type) {
      config.case_type = this.accessoryModal.case_type;
    }
    const dependencyMeta = this.buildAccessoryDependencyMeta(accessoryId);

    // Add to selected accessories with loading state
    const accessory = {
      id: accessoryId,
      config: config,
      displayValue: this.buildAccessoryDisplayValue(accessoryId, config),
      products: [],
      productData: null,
      loading: true,
      dependencyScope: dependencyMeta.dependencyScope,
      dependsOn: dependencyMeta.dependsOn
    };
    this.selectedAccessories.push(accessory);
    this.requestSideCartPositionRecalc();
    const customizeAvailabilityPromise = this.refreshAccessoryCustomizeAvailability(accessoryId, config);

    // Close modal
    this.closeModal();

    // Make API call to get product data for this accessory
    await this.fetchModalAccessoryProduct(accessoryId, config);
    await customizeAvailabilityPromise;
    this.scheduleAccessorySnapshotRefresh();
  },

  /**
   * Fetch product data for a modal-type accessory via API
   * @param {string} accessoryId - The accessory button ID
   * @param {object} config - The accessory config (make, model, case_type)
   */
  async fetchModalAccessoryProduct(accessoryId, config) {
    if (!this.isAccessorySelected(accessoryId)) return;

    const requestToken = this.nextAccessoryRequestToken('modal-product', accessoryId);
    const canApplyResult = () => this.isAccessoryRequestTokenActive(requestToken) && this.isAccessorySelected(accessoryId);

    try {
      // Build request body manually to include only this specific accessory
      // This ensures we get the exact product for this selection
      const body = {};

      // Add vehicle context
      if (this.context.year) body.year = this.context.year;
      if (this.context.make) body.make = this.context.make;
      if (this.context.model) body.model = this.context.model;
      if (this.context.trim_level) body.trim_level = this.context.trim_level;

      // Add tablet context if configured
      if (this.context.tablet_make) {
        body.tablet_make = this.context.tablet_make;
        body.tablet_model = this.context.tablet_model;
        body.tablet_case_type = this.context.tablet_case_type;
        body.tablet = {
          make: this.context.tablet_make,
          model: this.context.tablet_model,
          case_type: this.context.tablet_case_type
        };
      }

      // Add laptop/other device if selected
      if (this.context.laptop_selected) body.laptop_selected = true;
      if (this.context.other_device_selected) body.other_device_selected = true;

      // Add ONLY this accessory to get its specific product
      body.accessories = {
        [accessoryId]: config
      };

      const data = await this.postToBuilder(this.getBuilderEndpoint(), body);
      if (!canApplyResult()) return;

      // Get the product handle from kit.accessories.products
      const accessoryProducts = data.kit?.accessories?.products || [];
      const productHandle = accessoryProducts[0]; // Get the first product

      if (productHandle) {
        // Update the accessory with the product handle
        const currentAccessory = this.getSelectedAccessory(accessoryId);
        const currentConfig = currentAccessory?.config || config || {};
        const nextDisplayValue = this.buildAccessoryDisplayValue(accessoryId, currentConfig, accessoryProducts);
        if (!canApplyResult()) return;
        this.replaceSelectedAccessory(accessoryId, {
          products: accessoryProducts,
          displayValue: nextDisplayValue
        });
        this.syncAccessorySelectionToSummaryKit(accessoryId);

        // Fetch the Shopify product data
        await this.fetchAccessoryProductData(accessoryId, productHandle);
      } else {
        console.warn(`[Tough-Bar Accessories] No product found for ${accessoryId}`);
        if (!canApplyResult()) return;
        this.replaceSelectedAccessory(accessoryId, {
          loading: false,
          error: 'No product found'
        });
      }
    } catch (err) {
      console.error(`[Tough-Bar Accessories] Failed to fetch modal accessory product:`, err);
      if (!canApplyResult()) return;
      this.replaceSelectedAccessory(accessoryId, {
        loading: false,
        error: err.message
      });
    }
  },

  /**
   * Update configurable accessory option
   * @param {string} accessoryId - The accessory button ID
   * @param {object} option - The selected configuration option
   */
  async updateAccessoryConfig(accessoryId, option) {
    const existing = this.getSelectedAccessory(accessoryId);
    if (!existing) return;

    const products = option?.products || [];
    const nextHandle = products[0];
    const cachedProduct = nextHandle ? this.products[nextHandle] : null;
    const nextDisplayValue = this.buildAccessoryDisplayValue(accessoryId, option || {}, products);

    this.replaceSelectedAccessory(accessoryId, {
      config: option || {},
      displayValue: nextDisplayValue,
      products: products,
      productData: cachedProduct || null,
      loading: !!nextHandle && !cachedProduct,
      error: null
    });
    this.syncAccessorySelectionToSummaryKit(accessoryId);

    if (!nextHandle || cachedProduct) {
      return;
    }

    await this.fetchAccessoryProductData(accessoryId, nextHandle);
  },

  /**
   * Open customize modal for a selected accessory
   * @param {string} accessoryId - The accessory button ID
   */
  async openAccessoryCustomizeModal(accessoryId, options = {}) {
    const force = !!options.force;
    const selected = this.getSelectedAccessory(accessoryId);
    const resolvedAccessoryId = selected?.id || accessoryId;

    let button = this.getAccessoryButton(resolvedAccessoryId);
    if (!button && this.isFinalKitState) {
      await this.ensureAccessoryButtonsCache();
      button = this.getAccessoryButton(resolvedAccessoryId);
    }

    if (this.isKeyboardAccessoryButton(button || resolvedAccessoryId)) {
      await this.openKeyboardAccessoryCustomizeModal(resolvedAccessoryId);
      return;
    }

    if (button?.type !== 'modal') {
      if (!force && !this.canShowAccessoryCustomize(resolvedAccessoryId)) return;
      const accessory = this.getSelectedAccessory(resolvedAccessoryId);
      if (!accessory) return;

      this.accessoryCustomizeLoading = true;
      this.accessoryCustomizeError = null;
      this.accessoryCustomization = null;
      this.accessoryCustomizeSelections = {};
      this.accessoryCustomizeTarget = resolvedAccessoryId;
      this.accessoryCustomizeSignature = null;
      this.accessoryCustomizePrimarySelectionBackup = null;
      this.openModal('accessory-customize', { accessoryId: resolvedAccessoryId });
      const requestToken = this.nextAccessoryRequestToken('customize-modal', resolvedAccessoryId);

      try {
        if (button?.type === 'configurable') {
          const localCustomization = this.buildConfigurableAccessoryCustomization(button, accessory);
          if (!localCustomization) {
            throw new Error('No configurable options available for this accessory.');
          }
          if (!this.isAccessoryRequestTokenActive(requestToken)) return;
          this.accessoryCustomization = localCustomization;
          this.accessoryCustomizeTarget = resolvedAccessoryId;
          this.initAccessoryCustomizeSelections(accessory);
          return;
        }

        const body = this.buildAccessoryCustomizeRequestBody(resolvedAccessoryId, accessory.config);
        const data = await this.postToBuilder(this.getBuilderEndpoint(), body);
        if (data?.error) {
          throw new Error(data.error);
        }

        if (!this.isAccessoryRequestTokenActive(requestToken)) return;
        this.accessoryCustomization = this.normalizeAccessoryCustomizationPayload(data);
        this.accessoryCustomizeTarget = resolvedAccessoryId;
        this.initAccessoryCustomizeSelections(accessory);
      } catch (err) {
        if (!this.isAccessoryRequestTokenActive(requestToken)) return;
        console.error('[Tough-Bar Accessories] Failed to load accessory customization:', err);
        this.accessoryCustomizeError = err.message || 'Failed to load accessory customization.';
      } finally {
        if (this.isAccessoryRequestTokenActive(requestToken)) {
          this.accessoryCustomizeLoading = false;
        }
      }
      return;
    }

    this.accessoryCustomizeLoading = false;
    this.accessoryCustomizeError = null;
    this.accessoryCustomization = null;
    this.accessoryCustomizeSelections = {};
    this.accessoryCustomizeSignature = null;
    this.accessoryCustomizeTarget = resolvedAccessoryId;
    this.accessoryModal = this.buildAccessoryModalState(resolvedAccessoryId, selected?.config || {});
    this.accessoryCustomizePrimarySelectionBackup = { ...this.accessoryModal };
    this.openModal('accessory-customize', { accessoryId: resolvedAccessoryId });
    await this.refreshAccessoryCustomizeForModalSelection({ force: true });
  },

  /**
   * Remove specific accessory
   * @param {string} accessoryId - The accessory button ID
   */
  async removeAccessory(accessoryId) {
    if (this.isKeyboardAccessoryButton(accessoryId)) {
      this.invalidateAccessoryRequests(accessoryId);
      const removed = await this.applyKeyboardAccessoryRemoval(accessoryId);
      if (!removed) return;

      this.clearAccessoryCustomizeAvailability(accessoryId);
      this.requestSideCartPositionRecalc();
      this.scheduleAccessorySnapshotRefresh();
      return;
    }

    const index = this.findSelectedAccessoryIndex(accessoryId);
    if (index !== -1) {
      const removedId = this.selectedAccessories[index]?.id || accessoryId;
      this.invalidateAccessoryRequests(removedId);
      this.selectedAccessories.splice(index, 1);
      this.clearAccessoryCustomizeAvailability(removedId);
      this.requestSideCartPositionRecalc();
      this.scheduleAccessorySnapshotRefresh();
    }
  },

  /**
   * Clear all selected accessories
   */
  clearAccessories() {
    this.selectedAccessories.forEach(accessory => this.invalidateAccessoryRequests(accessory?.id));
    this.selectedAccessories = [];
    this.requestSideCartPositionRecalc();
    this.clearAccessoryCustomizeAvailability();
    this.accessoryModal = {
      accessoryId: null,
      make: '',
      model: '',
      case_type: ''
    };
    this.accessoryCustomizeSignature = null;
    this.accessoryCustomizePrimarySelectionBackup = null;

    if (this._accessorySnapshotRefreshTimer) {
      clearTimeout(this._accessorySnapshotRefreshTimer);
      this._accessorySnapshotRefreshTimer = null;
    }
    this._accessorySnapshotRefreshPending = false;

    this.scheduleAccessorySnapshotRefresh();
  },
};
